<template>
    <div class="flex items-center gap-2">
        <CreateButton
            :title="title"
            type="button"
            :disabled="disabled"
            class="disabled"
            @click.stop="open"
        />
        <a
            v-if="(url || newUrl) && (fileName || newFileName)"
            class="text-blue-500 hover:text-blue-600"
            :href="url || newUrl"
            target="blank"
            >{{ fileName || newFileName }}</a
        >
        <div v-if="deletable">
            <button
                v-if="fileName || newFileName"
                class="btn text-danger-600 border-gray-200 bg-white hover:border-gray-300"
                :title="$t('delete')"
                @click.stop="handelDelete"
            >
                <fa icon="fa-solid fa-xmark" />
            </button>
            <ModalWrapper v-if="isModalOpen" />
        </div>
        <EditButton
            v-if="url !== null"
            :title="title"
            type="button"
            @click.stop="handleOpenFileEditorModal"
        />
    </div>
    <FileEditorModal
        v-if="url !== null && accept === '.pdf'"
        id="file-editor-modal"
        ref="fileEditorModal"
        v-model:open="FileEditorModalOpen"
        v-model:placeholders="placeholders"
        :file-name="fileName"
        :url="url"
        :title="editorTitle"
        @submit="handleSubmitEditorModal"
    />
</template>

<script setup lang="ts">
import { ModalButtonStyles, useModal } from '@src/composables/common'
import CreateButton from '@src/partials/actions/CreateButton.vue'
import EditButton from '@src/partials/actions/EditButton.vue'
import FileEditorModal from '@src/partials/files/FileEditorModal.vue'
import { Placeholder } from '@src/types/Placeholder'
import {
    uploadECSP,
    uploadKYC,
    uploadPrivate,
    uploadPublic,
} from '@src/utils/api/s3'

import { useToastStore } from '@src/utils/stores/toast'
import { useFileDialog, useVModels } from '@vueuse/core'
import { computed, h, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export type FileUploadMode =
    | 'private'
    | 'public'
    | 'KYC'
    | 'ECSP'
    | 'MembershipDocument'

const { t } = useI18n()
const editorTitle = computed((): string => `Edit ${t(title.value)}`)
const newFileName = ref<string | null>(null)
const newUrl = ref<string>('')

const props = withDefaults(
    defineProps<{
        mode?: FileUploadMode
        title?: string
        accept?: string
        fileName?: string | null
        url?: string | null
        deletable?: boolean
        disabled?: boolean
    }>(),
    {
        mode: 'private',
        title: 'upload',
        accept: '',
        fileName: null,
        url: null,
        deletable: true,
        disabled: false,
    }
)

const emit = defineEmits(['submit', 'delete'])

const placeholders = computed((): Placeholder[] | null => {
    const placeholdersList: Placeholder[] = []

    const orderPlaceholders = [
        { key: 'salutation', value: '{{salutation}}' },
        { key: 'investor_full_name', value: '{{investor_full_name}}' },
        { key: 'investor_first_name', value: '{{investor_first_name}}' },
        { key: 'investor_last_name', value: '{{investor_last_name}}' },
        { key: 'investor_full_address', value: '{{investor_full_address}}' },
        { key: 'investor_address', value: '{{investor_address}}' },
        { key: 'investor_postal_code', value: '{{investor_postal_code}}' },
        { key: 'investor_city', value: '{{investor_city}}' },
        { key: 'investor_country', value: '{{investor_country}}' },
        { key: 'investor_email', value: '{{investor_email}}' },
        { key: 'investor_nationality', value: '{{investor_nationality}}' },
        { key: 'company_name', value: '{{company_name}}' },
        {
            key: 'company_registration_number',
            value: '{{company_registration_number}}',
        },
        { key: 'company_full_address', value: '{{company_full_address}}' },
        { key: 'company_address', value: '{{company_address}}' },
        { key: 'company_postal_code', value: '{{company_postal_code}}' },
        { key: 'company_city', value: '{{company_city}}' },
        { key: 'company_country', value: '{{company_country}}' },
        { key: 'depot_owner', value: '{{depot_owner}}' },
        { key: 'depot_number', value: '{{depot_number}}' },
        { key: 'depot_bank', value: '{{depot_bank}}' },
        { key: 'depot_bic', value: '{{depot_bic}}' },
        { key: 'iban', value: '{{iban}}' },
        { key: 'bic', value: '{{bic}}' },
        { key: 'pieces', value: '{{pieces}}' },
        { key: 'step_level', value: '{{step_level}}' },
        { key: 'total_amount', value: '{{total_amount}}' },
        { key: 'amount', value: '{{amount}}' },
        { key: 'total_amount_value', value: '{{total_amount_value}}' },
        { key: 'order_date', value: '{{order_date}}' },
        { key: 'order_time', value: '{{order_time}}' },
        { key: 'order_id', value: '{{order_id}}' },
        { key: 'order_reference_number', value: '{{order_reference_number}}' },
        { key: 'acceptance_date', value: '{{acceptance_date}}' },
        { key: 'campaign_title', value: '{{campaign_title}}' },
        {
            key: 'full_name_or_company_name',
            value: '{{full_name_or_company_name}}',
        },
        {
            key: 'date_of_birth_or_company_registration_number',
            value: '{{date_of_birth_or_company_registration_number}}',
        },
        {
            key: 'investor_full_address_or_company_address',
            value: '{{investor_full_address_or_company_address}}',
        },
        { key: 'asset_title', value: '{{asset_title}}' },
        { key: 'asset_status', value: '{{asset_status}}' },
        { key: 'asset_sub_status', value: '{{asset_sub_status}}' },
        { key: 'asset_location_city', value: '{{asset_location_city}}' },
        { key: 'asset_company_name', value: '{{asset_company_name}}' },
        { key: 'deposit_account_holder', value: '{{deposit_account_holder}}' },
        {
            key: 'deposit_account_holder_address',
            value: '{{deposit_account_holder_address}}',
        },
        {
            key: 'deposit_account_holder_country',
            value: '{{deposit_account_holder_country}}',
        },
        { key: 'asset_company_iban', value: '{{asset_company_iban}}' },
        { key: 'asset_company_bic', value: '{{asset_company_bic}}' },
        {
            key: 'wphg_appropriateness_notice',
            value: '{{wphg_appropriateness_notice}}',
        },
        {
            key: 'wphg_appropriateness_title',
            value: '{{wphg_appropriateness_title}}',
        },
        {
            key: 'wphg_appropriateness_checkbox',
            value: '{{wphg_appropriateness_checkbox}}',
        },
    ]

    const assetSignaturePlaceholders = [
        {
            key: 'SIGNATURE',
            value: '{{SIGNATURE}}',
            type: 'signature',
        },
    ]

    const certificatePlaceholders = [
        { key: 'contract_type', value: '{{contract_type}}' },
        { key: 'interest_rate', value: '{{interest_rate}}' },
        { key: 'membership_number', value: '{{membership_number}}' },
        { key: 'total_number_of_share', value: '{{total_number_of_share}}' },
    ]

    const membershipPlaceholders = [
        {
            key: 'epc_qr_code_data_url',
            value: '{{epc_qr_code_data_url}}',
            type: 'qr-code',
        },
    ]

    const taxPlaceholders = [
        { key: 'issuer', value: '{{issuer}}' },
        { key: 'investor_name', value: '{{investor_name}}' },
        { key: 'contract_type', value: '{{contract_type}}' },
        { key: 'interest_bonus', value: '{{interest_bonus}}' },
        { key: 'interest_rate', value: '{{interest_rate}}' },
        { key: 'investment', value: '{{investment}}' },
        { key: 'investment_amount', value: '{{investment_amount}}' },
        { key: 'investment_bic', value: '{{investment_bic}}' },
        { key: 'investment_iban', value: '{{investment_iban}}' },
        { key: 'paid_bonus', value: '{{paid_bonus}}' },
        { key: 'paid_clearance', value: '{{paid_clearance}}' },
        { key: 'paid_interest', value: '{{paid_interest}}' },
        {
            key: 'paid_interest_excluding_bonus',
            value: '{{paid_interest_excluding_bonus}}',
        },
        { key: 'paid_interest_bonus', value: '{{paid_interest_bonus}}' },
        { key: 'paid_rest', value: '{{paid_rest}}' },
        { key: 'paid_rest_clearance', value: '{{paid_rest_clearance}}' },
        { key: 'paid_rest_interest', value: '{{paid_rest_interest}}' },
        { key: 'paid_total_gross', value: '{{paid_total_gross}}' },
        { key: 'paid_total_net', value: '{{paid_total_net}}' },
        { key: 'total_amount_to_pay', value: '{{total_amount_to_pay}}' },
        { key: 'voucher_amount', value: '{{voucher_amount}}' },
        { key: 'wallet_amount', value: '{{wallet_amount}}' },
        { key: 'paid_interest_total', value: '{{paid_interest_total}}' },
        { key: 'paid_bonus_total', value: '{{paid_bonus_total}}' },
        { key: 'paid_clearance_total', value: '{{paid_clearance_total}}' },
        { key: 'previous_interest_date', value: '{{previous_interest_date}}' },
        { key: 'current_interest_date', value: '{{current_interest_date}}' },
        {
            key: 'investment_acceptance_date',
            value: '{{investment_acceptance_date}}',
        },
        {
            key: 'interest_calculation_start_date',
            value: '{{interest_calculation_start_date}}',
        },
        { key: 'investor_address', value: '{{investor_address}}' },
        { key: 'investor_zip', value: '{{investor_zip}}' },
        { key: 'investor_city', value: '{{investor_city}}' },
        { key: 'investor_country', value: '{{investor_country}}' },
        { key: 'gross_interest', value: '{{gross_interest}}' },
        { key: 'net_interest', value: '{{net_interest}}' },
        { key: 'tax_amount', value: '{{tax_amount}}' },
        { key: 'tax_rate', value: '{{tax_rate}}' },
        { key: 'tax_details', value: '{{tax_details}}' },
    ]

    if (props.title === 'asset_signature_document') {
        placeholdersList.push(...assetSignaturePlaceholders)
        placeholdersList.push(...orderPlaceholders)
    }

    if (props.title === 'acceptance_document') {
        placeholdersList.push(...orderPlaceholders)
    }

    if (props.title === 'certificate_document') {
        placeholdersList.push(...orderPlaceholders)
        placeholdersList.push(...certificatePlaceholders)
    }

    if (props.title === 'membership_document') {
        placeholdersList.push(...orderPlaceholders)
        placeholdersList.push(...membershipPlaceholders)
    }

    if (props.title === 'tax_document') {
        placeholdersList.push(...taxPlaceholders)
        placeholdersList.push(...orderPlaceholders)
    }

    return placeholdersList.length ? placeholdersList : null
})

const { mode, title, accept, fileName, deletable } = useVModels(props, emit)

const { open, files, reset } = useFileDialog({
    multiple: false,
    accept: accept.value,
})

const i18n = useI18n()

const toast = useToastStore()

const isModalOpen = ref(false)
const FileEditorModalOpen = ref<boolean>(false)

const { ModalWrapper, openModal } = useModal()

function handelDelete() {
    isModalOpen.value = true
    openModal(
        {
            id: 'delete_file',
            title: 'modal_title_delete_file',
            open: isModalOpen,
            onCancel: () => (isModalOpen.value = false),
            onSubmit: () => {
                emit('delete')
                newFileName.value = null
                isModalOpen.value = false
            },
        },
        () =>
            h(
                'span',
                i18n.t('modal_text_delete_file', { file: fileName.value })
            ),
        {
            styles: {
                submitButton: ModalButtonStyles['RED'],
            },
        }
    )
}

function handleOpenFileEditorModal() {
    FileEditorModalOpen.value = true
}

async function handleSubmitEditorModal(file: File) {
    try {
        await uploadFile(file)
        toast.success(i18n.t('success_edited_file_uploaded'))
        FileEditorModalOpen.value = false
    } catch (error) {
        toast.error(i18n.t('error_edited_file_uploaded'))
    }
}

async function uploadFile(file: File) {
    if (file) {
        newFileName.value = file.name

        if (mode.value === 'private' || mode.value === 'MembershipDocument') {
            const response = await uploadPrivate(file)
            newUrl.value = response.data.temp_storage_url
            emit('submit', response.data, newFileName.value)
        }

        if (mode.value === 'public') {
            const response = await uploadPublic(file)
            emit('submit', response.data, newFileName.value)
        }

        if (mode.value === 'KYC') {
            const response = await uploadKYC(file)
            emit('submit', response, newFileName.value)
        }

        if (mode.value === 'ECSP') {
            const response = await uploadECSP(file)
            emit('submit', response, newFileName.value)
        }
    }
}

watch(files, async function (files) {
    if (!files) {
        return
    }
    const file = files?.item(0)
    if (file) {
        uploadFile(file)
    }
    reset()
})
</script>
